import { GRAPH_API_BASE_URL } from '../configs/appConfig';

interface IInternetHeadersCapableItem { getAllInternetHeadersAsync?: (callback: (asyncResult: Office.AsyncResult<string>) => void) => void; }

// verify RFC 2822 internet message ID structure
export const isValidInternetMessageId = (id: string): boolean => {
    const trimmed = id?.trim();
    return Boolean(trimmed?.startsWith('<') && trimmed?.endsWith('>'));
};

export const normalizeInternetMessageId = (id?: string | null): string => {
    const trimmed = id?.trim() ?? '';
    return isValidInternetMessageId(trimmed) ? trimmed : '';
};

const extractInternetMessageIdFromHeaders = (headers: string): string => {
    const lines = headers.split(/\r?\n/);
    let messageIdHeaderValue = '';

    for (let index = 0; index < lines.length; index += 1) {
        const currentLine = lines[index];
        const separatorIndex = currentLine.indexOf(':');
        if (separatorIndex <= 0) {
            continue;
        }

        const headerName = currentLine.slice(0, separatorIndex).trim().toLowerCase();
        if (headerName !== 'message-id') {
            continue;
        }

        let value = currentLine.slice(separatorIndex + 1).trim();
        let continuationIndex = index + 1;
        while (continuationIndex < lines.length) {
            const continuationLine = lines[continuationIndex];
            if (!continuationLine.startsWith(' ') && !continuationLine.startsWith('\t')) {
                break;
            }

            value += ` ${continuationLine.trim()}`;
            continuationIndex += 1;
        }

        messageIdHeaderValue = value;
        break;
    }

    if (!messageIdHeaderValue) {
        return '';
    }

    const leftBracketIndex = messageIdHeaderValue.indexOf('<');
    const rightBracketIndex = messageIdHeaderValue.indexOf('>', leftBracketIndex + 1);
    const angleBracketValue = leftBracketIndex >= 0 && rightBracketIndex > leftBracketIndex
        ? messageIdHeaderValue.slice(leftBracketIndex, rightBracketIndex + 1)
        : messageIdHeaderValue;
    return normalizeInternetMessageId(angleBracketValue);
};

const getInternetMessageIdFromHeaders = async (): Promise<string> => {
    const mailboxItem = Office.context.mailbox.item as IInternetHeadersCapableItem | null | undefined;
    const getAllInternetHeadersAsync = mailboxItem?.getAllInternetHeadersAsync;
    if (!getAllInternetHeadersAsync) {
        return '';
    }

    return await new Promise((resolve) => {
        try {
            getAllInternetHeadersAsync((asyncResult: Office.AsyncResult<string>) => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    resolve('');
                    return;
                }

                const headers = asyncResult.value ?? '';
                resolve(extractInternetMessageIdFromHeaders(headers));
            });
        } catch {
            resolve('');
        }
    });
};

export const getCurrentInternetMessageId = async (): Promise<string> => {
    const directInternetMessageId = normalizeInternetMessageId(Office.context.mailbox.item?.internetMessageId);
    if (directInternetMessageId) {
        return directInternetMessageId;
    }

    return await getInternetMessageIdFromHeaders();
};

export type GetSelectedMessageIdsResult = { itemIds: string[]; skippedCount: number };

const fetchSelectedItems = (): Promise<Office.SelectedItemDetails[]> =>
    new Promise((resolve) => {
        try {
            Office.context.mailbox.getSelectedItemsAsync((asyncResult: Office.AsyncResult<Office.SelectedItemDetails[]>) => {
                resolve(asyncResult.status === Office.AsyncResultStatus.Succeeded ? (asyncResult.value ?? []) : []);
            });
        } catch {
            resolve([]);
        }
    });

const fetchInternetMessageIdFromGraph = async (accessToken: string, exchangeItemId: string, targetInbox?: string | null): Promise<string> => {
    try {
        const restId = Office.context.mailbox.convertToRestId(exchangeItemId, Office.MailboxEnums.RestVersion.v2_0);
        const mailboxSegment = targetInbox ? `users/${encodeURIComponent(targetInbox)}` : 'me';
        const response = await fetch(
            `${GRAPH_API_BASE_URL}/${mailboxSegment}/messages/${encodeURIComponent(restId)}?$select=internetMessageId`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!response.ok) {
            return '';
        }
        const data = await response.json() as { internetMessageId?: string };
        return normalizeInternetMessageId(data.internetMessageId);
    } catch {
        return '';
    }
};

export const getSelectedMessageIdsResult = async (accessToken?: string, targetInbox?: string | null): Promise<GetSelectedMessageIdsResult> => {
    const fallbackItemId = await getCurrentInternetMessageId();
    const fallbackItemIds = fallbackItemId ? [fallbackItemId] : [];
    const supportsMultiSelect = Office.context.requirements?.isSetSupported?.('Mailbox', '1.13');

    if (!supportsMultiSelect || !Office.context.mailbox.getSelectedItemsAsync) {
        return { itemIds: fallbackItemIds, skippedCount: 0 };
    }

    const allItems = await fetchSelectedItems();
    if (allItems.length === 0) {
        return { itemIds: fallbackItemIds, skippedCount: 0 };
    }

    const directIds: string[] = [];
    const exchangeIdsForGraphFallback: string[] = [];

    for (const item of allItems) {
        const id = normalizeInternetMessageId(item.internetMessageId);
        if (id) {
            directIds.push(id);
        } else if (accessToken && item.itemId) {
            exchangeIdsForGraphFallback.push(item.itemId);
        }
    }

    const graphIds = accessToken && exchangeIdsForGraphFallback.length > 0
        ? await Promise.all(exchangeIdsForGraphFallback.map((exchangeId) => fetchInternetMessageIdFromGraph(accessToken, exchangeId, targetInbox)))
        : [];

    const resolvedGraphIds = graphIds.filter((id) => id.length > 0);
    const skippedCount = Math.max(0, allItems.length - directIds.length - resolvedGraphIds.length);
    const uniqueValidIds = Array.from(new Set([...directIds, ...resolvedGraphIds]));

    return { itemIds: uniqueValidIds.length > 0 ? uniqueValidIds : fallbackItemIds, skippedCount };
};

export const getSelectedMessageIds = async (accessToken?: string, targetInbox?: string | null): Promise<string[]> =>
    (await getSelectedMessageIdsResult(accessToken, targetInbox)).itemIds;

export const GetItemRestId = (): string => Office.context.mailbox.item?.internetMessageId ?? '';
